import { createFileRoute, useNavigate, Navigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { CompanyId } from '@igf-minar/shared'
import { api } from '../lib/api'
import { useCurrentUser, isAdminRole } from '../lib/auth'
import { getRecentCompanies, pushRecentCompany } from '../lib/recent'
import { usePreferences } from '../lib/preferences'
import { Icon, type IconName } from '../components/layout/Icon'
import { spark, geom } from '../lib/chart'
import { formatDateLong, formatDateShort } from '../lib/dates'

export const Route = createFileRoute('/')({
  component: DashboardPage,
})

function DashboardPage() {
  const navigate = useNavigate()
  const { data: user } = useCurrentUser()
  const admin = isAdminRole(user)
  const year = new Date().getFullYear()
  const [recent] = useState(() => getRecentCompanies())
  const { favorites } = usePreferences()

  const companiesQuery = useQuery({
    queryKey: ['companies'],
    queryFn: () => api.companies(),
    staleTime: 5 * 60 * 1000,
  })
  const summaryQuery = useQuery({
    queryKey: ['activity-summary'],
    queryFn: () => api.admin.activity.summary(),
    enabled: admin,
    staleTime: 60 * 1000,
  })

  const companies = companiesQuery.data?.companies ?? []
  const liveCount = companies.filter((c) => c.dataSource === 'live').length
  const summary = summaryQuery.data
  const daily = summary?.daily ?? []
  const greeting = user?.claims?.displayName?.split(' ')[0] || user?.email?.split('@')[0] || ''

  const today = formatDateLong(new Date())

  function openCompany(c: { id: string; name: string; shortName: string }) {
    pushRecentCompany({ id: c.id, name: c.name, shortName: c.shortName })
    navigate({ to: '/companies/$id', params: { id: c.id as CompanyId }, search: { year } })
  }

  // The Hub dashboard is admin/superadmin only — regular customer users land on
  // their customer list instead.
  if (user && !admin) {
    return <Navigate to="/customers" replace />
  }

  return (
    <div className="max-w-[1240px] mx-auto px-6 sm:px-10 py-8 pb-16 igf-fade flex flex-col gap-[26px]">
      {/* Header */}
      <div className="flex items-end justify-between gap-5 flex-wrap">
        <div>
          <h1 className="font-grotesk font-bold text-[30px] leading-tight text-[var(--fg)] tracking-tight">
            Halló{greeting ? `, ${greeting}` : ''} <span className="font-sans">👋</span>
          </h1>
          <p className="mt-2 text-[15px] font-medium text-[var(--muted)]">
            Yfirlit yfir viðskiptavini og virkni Íslenska gámafélagsins.
          </p>
        </div>
        <div className="flex items-center gap-2 text-[13px] font-medium text-[var(--muted)] bg-[var(--surface)] border border-[var(--border)] rounded-[10px] px-3.5 py-2.5">
          <Icon name="calendar" className="w-[18px] h-[18px] text-[var(--brand)]" />
          {today}
        </div>
      </div>

      {/* KPI ROW */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-[18px]">
        <KpiCard
          label="Viðskiptavinir"
          value={companies.length}
          icon="groups"
          accent="var(--acc-green)"
          soft="var(--acc-green-soft)"
          hint={`${liveCount} með lifandi API`}
          hintDot="var(--acc-green)"
          title="Heildarfjöldi viðskiptavina — smelltu til að sjá alla viðskiptavini"
          onClick={() => navigate({ to: '/customers' })}
        />
        {admin ? (
          <>
            <KpiCard
              label="Virkir notendur · 14d"
              value={summary?.uniqueUsers}
              icon="person"
              accent="var(--acc-blue)"
              soft="var(--acc-blue-soft)"
              hint="síðustu 14 daga"
              sparkVals={daily.length > 1 ? daily.map((d) => d.users) : undefined}
              title="Einstakir notendur sem skráðu sig inn síðustu 14 daga — smelltu til að skoða virkni"
              onClick={() => navigate({ to: '/admin/activity' })}
            />
            <KpiCard
              label="Innskráningar · 14d"
              value={summary?.totalLogins}
              icon="login"
              accent="var(--acc-violet)"
              soft="var(--acc-violet-soft)"
              hint={daily.length ? undefined : 'engin gögn'}
              sparkVals={daily.length > 1 ? daily.map((d) => d.logins) : undefined}
              title="Heildarfjöldi innskráninga síðustu 14 daga — smelltu til að skoða virkni"
              onClick={() => navigate({ to: '/admin/activity' })}
            />
            <KpiCard
              label="Lotur · 14d"
              value={summary?.totalSessions}
              icon="timer"
              accent="var(--acc-amber)"
              soft="var(--acc-amber-soft)"
              hint={summary ? `meðal ${formatDuration(summary.avgDurationSeconds)}` : undefined}
              title="Fjöldi innskráðra lota síðustu 14 daga (og meðallengd) — smelltu til að skoða virkni"
              onClick={() => navigate({ to: '/admin/activity' })}
            />
          </>
        ) : (
          <KpiCard
            label="Lifandi gögn"
            value={liveCount}
            icon="apartment"
            accent="var(--acc-blue)"
            soft="var(--acc-blue-soft)"
            hint="fyrirtæki með SVR2"
            title="Fyrirtæki með lifandi SVR2-tengingu — smelltu til að sjá viðskiptavini"
            onClick={() => navigate({ to: '/customers' })}
          />
        )}
      </div>

      {/* MAIN GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.65fr_1fr] gap-[18px] items-start">
        {/* LEFT */}
        <div className="flex flex-col gap-[18px]">
          {admin && daily.length > 1 && <LoginAreaChart daily={daily} />}

          {favorites.length > 0 && (
            <Panel title="Eftirlætin þín — smelltu til að opna">
              <div className="flex items-center justify-between mb-3.5">
                <h3 className="font-grotesk font-bold text-base text-[var(--fg)]">★ Eftirlæti</h3>
                <button
                  onClick={() => navigate({ to: '/customers' })}
                  className="font-semibold text-[13px] text-[var(--brand)] hover:text-[var(--brand-strong)]"
                >
                  Breyta á viðskiptavinalista →
                </button>
              </div>
              <div className="flex flex-col gap-2">
                {favorites
                  .map((id) => companies.find((c) => c.id === id))
                  .filter((c): c is NonNullable<typeof c> => !!c)
                  .map((c) => (
                    <button
                      key={c.id}
                      onClick={() => openCompany(c)}
                      className="group flex items-center gap-3.5 w-full text-left border border-[var(--border)] rounded-xl px-3.5 py-3 hover:border-[var(--brand)] hover:bg-[var(--surface-2)] transition"
                    >
                      <span className="grid h-10 w-10 flex-none place-items-center rounded-[10px] bg-amber-100 font-grotesk text-[15px] font-bold text-amber-600">
                        ★
                      </span>
                      <span className="flex flex-col gap-0.5 flex-1 min-w-0">
                        <span className="truncate font-bold text-sm text-[var(--fg)]">{c.name}</span>
                        <span className="truncate text-[12.5px] font-medium text-[var(--muted)]">{c.shortName}</span>
                      </span>
                      <Icon name="chevron_right" className="w-5 h-5 text-[var(--faint)]" />
                    </button>
                  ))}
              </div>
            </Panel>
          )}

          <Panel title="Viðskiptavinir sem þú hefur skoðað nýlega — smelltu til að opna">
            <div className="flex items-center justify-between mb-3.5">
              <h3 className="font-grotesk font-bold text-base text-[var(--fg)]">Nýlega skoðað</h3>
              <button
                onClick={() => navigate({ to: '/customers' })}
                className="font-semibold text-[13px] text-[var(--brand)] hover:text-[var(--brand-strong)]"
              >
                Allir viðskiptavinir →
              </button>
            </div>
            {recent.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[var(--border)] p-8 text-center text-sm text-[var(--faint)]">
                Engir viðskiptavinir skoðaðir enn. Notaðu leitina að ofan til að opna viðskiptavin.
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {recent.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => openCompany(c)}
                    className="group flex items-center gap-3.5 w-full text-left border border-[var(--border)] rounded-xl px-3.5 py-3 hover:border-[var(--brand)] hover:bg-[var(--surface-2)] transition"
                  >
                    <span className="grid h-10 w-10 flex-none place-items-center rounded-[10px] bg-[var(--brand-soft)] font-grotesk text-[15px] font-bold text-[var(--brand)]">
                      {c.shortName.charAt(0)}
                    </span>
                    <span className="flex flex-col gap-0.5 flex-1 min-w-0">
                      <span className="truncate font-bold text-sm text-[var(--fg)]">{c.name}</span>
                      <span className="truncate text-[12.5px] font-medium text-[var(--muted)]">{c.shortName}</span>
                    </span>
                    <span className="font-grotesk text-[11px] font-semibold text-[var(--muted)]">
                      {timeAgo(c.at)}
                    </span>
                    <Icon name="chevron_right" className="w-5 h-5 text-[var(--faint)]" />
                  </button>
                ))}
              </div>
            )}
          </Panel>
        </div>

        {/* RIGHT */}
        <div className="flex flex-col gap-[18px]">
          <Panel title="Flýtileiðir í algengar aðgerðir">
            <h3 className="font-grotesk font-bold text-base text-[var(--fg)] mb-3.5">Fljótleg stök</h3>
            <div className="flex flex-col gap-2">
              <QuickAction icon="apartment" label="Allir viðskiptavinir" onClick={() => navigate({ to: '/customers' })} />
              {admin && (
                <QuickAction
                  icon="manage_accounts"
                  label="Stofna / stýra notendum"
                  onClick={() => navigate({ to: '/admin/users' })}
                />
              )}
              {admin && (
                <QuickAction
                  icon="monitoring"
                  label="Skoða virkni"
                  onClick={() => navigate({ to: '/admin/activity' })}
                />
              )}
            </div>
          </Panel>

          <Panel title="Hlutfall viðskiptavina með lifandi SVR2 API-tengingu">
            <h3 className="font-grotesk font-bold text-base text-[var(--fg)]">API-tengingar</h3>
            <p className="mt-1 mb-3 text-[12.5px] font-medium text-[var(--muted)]">
              Viðskiptavinir með lifandi tengingu
            </p>
            <ApiDonut live={liveCount} total={companies.length} />
          </Panel>
        </div>
      </div>
    </div>
  )
}

function Panel({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <div
      title={title}
      className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-5 sm:p-[22px] shadow-[var(--elev)]"
    >
      {children}
    </div>
  )
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds < 1) return '0s'
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

function timeAgo(then: number): string {
  if (!then || Number.isNaN(then)) return ''
  const day = 24 * 60 * 60 * 1000
  const diff = Date.now() - then
  if (new Date(then).toDateString() === new Date().toDateString()) return 'í dag'
  const days = Math.floor(diff / day)
  if (days <= 1) return 'í gær'
  return `${days} d síðan`
}

function KpiCard({
  label,
  value,
  icon,
  accent,
  soft,
  hint,
  hintDot,
  sparkVals,
  onClick,
  title,
}: {
  label: string
  value: number | undefined
  icon: IconName
  accent: string
  soft: string
  hint?: string
  hintDot?: string
  sparkVals?: number[]
  onClick?: () => void
  title?: string
}) {
  const sp = sparkVals ? spark(sparkVals) : null
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      title={title}
      className={`relative overflow-hidden text-left bg-[var(--surface)] border border-[var(--border)] rounded-2xl px-[18px] pt-[18px] pb-3.5 shadow-[var(--elev)] transition hover:-translate-y-[3px] hover:shadow-[var(--shadow-lift)] hover:border-[var(--border-strong)] ${
        onClick ? 'cursor-pointer' : ''
      }`}
      style={{ color: accent }}
    >
      <span className="absolute inset-y-0 left-0 w-1" style={{ background: accent }} />
      <div className="flex items-center justify-between gap-2">
        <span className="font-grotesk font-bold text-[11px] uppercase tracking-wider text-[var(--muted)]">
          {label}
        </span>
        <span
          className="w-8 h-8 rounded-[9px] grid place-items-center flex-none"
          style={{ background: soft }}
        >
          <Icon name={icon} className="w-[19px] h-[19px]" />
        </span>
      </div>
      <div className="flex items-end justify-between gap-2 mt-2">
        <div>
          <div className="font-grotesk font-bold text-[34px] leading-none text-[var(--fg)]">
            {value === undefined ? '–' : new Intl.NumberFormat('is-IS').format(value)}
          </div>
          {hint && (
            <div className="mt-[7px] flex items-center gap-1.5 font-semibold text-[12px] text-[var(--muted)]">
              {hintDot && <span className="w-[7px] h-[7px] rounded-full" style={{ background: hintDot }} />}
              {hint}
            </div>
          )}
        </div>
        {sp && (
          <svg viewBox="0 0 120 42" preserveAspectRatio="none" className="w-24 h-[38px] flex-none">
            <path d={sp.area} fill="currentColor" fillOpacity="0.12" />
            <path
              d={sp.line}
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        )}
      </div>
    </Tag>
  )
}

function QuickAction({ icon, label, onClick }: { icon: IconName; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="group flex items-center gap-3 w-full text-left border border-[var(--border)] rounded-[11px] px-3.5 py-3 text-[var(--fg)] hover:border-[var(--brand)] hover:bg-[var(--brand-soft)] transition"
    >
      <span className="w-[34px] h-[34px] flex-none rounded-[9px] bg-[var(--surface-2)] text-[var(--brand)] grid place-items-center">
        <Icon name={icon} className="w-[19px] h-[19px]" />
      </span>
      <span className="flex-1 font-semibold text-sm">{label}</span>
      <Icon name="arrow_forward" className="w-[19px] h-[19px] text-[var(--faint)]" />
    </button>
  )
}

function ApiDonut({ live, total }: { live: number; total: number }) {
  const pct = total > 0 ? live / total : 0
  const C = 2 * Math.PI * 50
  const filled = C * pct
  return (
    <div className="flex items-center gap-5">
      <div className="relative w-[110px] h-[110px] flex-none">
        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
          <circle cx="60" cy="60" r="50" fill="none" stroke="var(--surface-2)" strokeWidth="13" />
          <circle
            cx="60"
            cy="60"
            r="50"
            fill="none"
            stroke="var(--brand)"
            strokeWidth="13"
            strokeLinecap="round"
            strokeDasharray={`${filled.toFixed(2)} ${(C - filled).toFixed(2)}`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-grotesk font-bold text-2xl leading-none text-[var(--fg)]">
            {Math.round(pct * 100)}%
          </span>
          <span className="font-grotesk font-semibold text-[10px] text-[var(--muted)]">
            {live} / {total}
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-3 flex-1">
        <LegendRow color="var(--brand)" label="Lifandi API" value={live} />
        <LegendRow
          color="var(--surface-2)"
          border="var(--border-strong)"
          label="Engin tenging"
          muted
          value={total - live}
        />
      </div>
    </div>
  )
}

function LegendRow({
  color,
  border,
  label,
  value,
  muted,
}: {
  color: string
  border?: string
  label: string
  value: number
  muted?: boolean
}) {
  return (
    <div className="flex items-center gap-2.5">
      <span
        className="w-2.5 h-2.5 rounded-[3px]"
        style={{ background: color, border: border ? `1px solid ${border}` : undefined }}
      />
      <span className={`font-semibold text-[13px] ${muted ? 'text-[var(--muted)]' : 'text-[var(--fg)]'}`}>
        {label}
      </span>
      <span className="ml-auto font-grotesk font-bold text-[13px] text-[var(--fg)]">{value}</span>
    </div>
  )
}

function LoginAreaChart({ daily }: { daily: { date: string; logins: number }[] }) {
  const [hoverI, setHoverI] = useState<number | null>(null)
  const W = 700
  const H = 240
  const pl = 14
  const pr = 14
  const pt = 18
  const pb = 26
  const vals = daily.map((d) => d.logins)
  const maxY = Math.max(2, ...vals)
  const g = useMemo(() => geom(vals, W, H, pl, pr, pt, pb, maxY), [daily])
  const ih = H - pt - pb
  const gridYs = [0, 1, 2, 3].map((k) => pt + ih - (k / 3) * ih)
  const n = daily.length
  const labelIdx = Array.from(new Set([0, Math.floor(n / 4), Math.floor(n / 2), Math.floor((3 * n) / 4), n - 1]))
  const hover = hoverI != null ? g.pts[hoverI] : null
  const trending = vals.length >= 2 && vals[vals.length - 1] >= vals[0]

  function onMove(e: React.MouseEvent<HTMLDivElement>) {
    const r = e.currentTarget.getBoundingClientRect()
    const frac = (e.clientX - r.left) / r.width
    const t = (frac * W - pl) / (W - pl - pr)
    let i = Math.round(t * (n - 1))
    i = Math.max(0, Math.min(n - 1, i))
    if (i !== hoverI) setHoverI(i)
  }

  function fmtDate(iso: string): string {
    return formatDateShort(iso)
  }

  return (
    <Panel title="Innskráningar á dag síðustu vikurnar — farðu yfir línuritið til að sjá dagsetningu og fjölda">
      <div className="flex items-start justify-between gap-3 mb-1.5">
        <div>
          <h3 className="font-grotesk font-bold text-base text-[var(--fg)]">Innskráningar</h3>
          <p className="mt-1 text-[12.5px] font-medium text-[var(--muted)]">Síðustu {n} dagar</p>
        </div>
        {trending && (
          <div className="flex items-center gap-1.5 font-semibold text-[12px] text-[var(--brand)] bg-[var(--brand-soft)] px-2.5 py-1.5 rounded-lg">
            <Icon name="trending_up" className="w-4 h-4" />
            Virkni að aukast
          </div>
        )}
      </div>

      <div
        className="relative w-full h-[230px] mt-1.5 cursor-crosshair text-[var(--brand)]"
        onMouseMove={onMove}
        onMouseLeave={() => setHoverI(null)}
      >
        <svg viewBox="0 0 700 240" preserveAspectRatio="none" className="absolute inset-0 w-full h-full block">
          <defs>
            <linearGradient id="igfArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.30" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </linearGradient>
          </defs>
          {gridYs.map((y, i) => (
            <line
              key={i}
              x1="14"
              y1={y}
              x2="686"
              y2={y}
              stroke="var(--border)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
              strokeDasharray="3 5"
            />
          ))}
          <path d={g.area} fill="url(#igfArea)" />
          <path
            d={g.line}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {hover && (
          <>
            <div
              className="absolute top-0 bottom-[22px] w-px bg-[var(--brand)] opacity-40"
              style={{ left: `${hover.pctX}%` }}
            />
            <div
              className="absolute w-3 h-3 rounded-full bg-[var(--brand)] border-[2.5px] border-[var(--surface)] shadow-md -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${hover.pctX}%`, top: `${hover.pctY}%` }}
            />
            <div
              className="absolute -translate-x-1/2 bg-[var(--fg)] text-[var(--surface)] px-2.5 py-1.5 rounded-lg font-semibold text-[12px] whitespace-nowrap shadow-lg pointer-events-none"
              style={{ left: `${hover.pctX}%`, top: `${hover.pctY}%`, transform: 'translate(-50%,-118%)' }}
            >
              <div className="font-grotesk font-bold text-[11px] opacity-75 mb-0.5">
                {fmtDate(daily[hover.i].date)}
              </div>
              {hover.val} innskráningar
            </div>
          </>
        )}

        <div className="absolute left-0 right-0 bottom-0 h-[18px]">
          {labelIdx.map((i) => (
            <span
              key={i}
              className="absolute -translate-x-1/2 font-grotesk font-semibold text-[10.5px] text-[var(--faint)]"
              style={{ left: `${g.pts[i].pctX}%` }}
            >
              {fmtDate(daily[i].date)}
            </span>
          ))}
        </div>
      </div>
    </Panel>
  )
}
